/**
 * Zero-Knowledge RFC 6238 Time-Based One-Time Password (TOTP) Authenticator
 * Built using native Web Crypto API HMAC-SHA1
 */

// Base32 RFC 4648 alphabet
const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

export function base32ToBytes(base32) {
  const clean = base32.replace(/[\s=-]/g, '').toUpperCase();
  let bits = '';
  for (let i = 0; i < clean.length; i++) {
    const val = BASE32_ALPHABET.indexOf(clean[i]);
    if (val === -1) continue;
    bits += val.toString(2).padStart(5, '0');
  }
  const bytes = new Uint8Array(Math.floor(bits.length / 8));
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(bits.slice(i * 8, (i + 1) * 8), 2);
  }
  return bytes;
}

export async function generateTotp(secret, timeStep = 30) {
  if (!secret) return { code: '000000', secondsRemaining: 30 };
  const nowSec = Math.floor(Date.now() / 1000);
  const counter = Math.floor(nowSec / timeStep);
  const secondsRemaining = timeStep - (nowSec % timeStep);

  try {
    const keyBytes = base32ToBytes(secret);
    if (keyBytes.length === 0) return { code: '000000', secondsRemaining };

    const cryptoKey = await window.crypto.subtle.importKey(
      'raw',
      keyBytes,
      { name: 'HMAC', hash: 'SHA-1' },
      false,
      ['sign']
    );

    // 8-byte big-endian counter buffer
    const buffer = new ArrayBuffer(8);
    const view = new DataView(buffer);
    view.setBigUint64(0, BigInt(counter));

    const signature = await window.crypto.subtle.sign('HMAC', cryptoKey, buffer);
    const sigBytes = new Uint8Array(signature);

    // Dynamic truncation
    const offset = sigBytes[sigBytes.length - 1] & 0x0f;
    const binary =
      ((sigBytes[offset] & 0x7f) << 24) |
      ((sigBytes[offset + 1] & 0xff) << 16) |
      ((sigBytes[offset + 2] & 0xff) << 8) |
      (sigBytes[offset + 3] & 0xff);

    const otp = (binary % 1000000).toString().padStart(6, '0');
    return { code: otp, secondsRemaining };
  } catch (err) {
    console.warn('TOTP generation error:', err);
    return { code: '000000', secondsRemaining };
  }
}
